"use client"

import { useEffect } from "react"

import {
  circularFontFamily,
  interFontFamily,
  precachedFontFamilies,
} from "@/components/font-faces"

export { interFontFamily }

// font.style.fontFamily and FontFace.family quote names differently; compare them bare
function bareFamily(family: string) {
  return family
    .split(",")[0]
    .trim()
    .replace(/^["']|["']$/g, "")
}

// Save-Data, or a connection Chrome rates slower than 4G. Other browsers don't say.
function onMeteredOrSlowConnection() {
  if (!("connection" in navigator)) return false
  const connection: unknown = navigator.connection
  if (typeof connection !== "object" || connection === null) return false
  if ("saveData" in connection && connection.saveData === true) return true
  return (
    "effectiveType" in connection &&
    typeof connection.effectiveType === "string" &&
    connection.effectiveType !== "4g"
  )
}

// After the page loads and goes idle, download the extended faces a later chat reply or
// heading is likely to need, so the text doesn't swap fonts when it arrives. Only the
// normal style: italics with those characters are rare enough to load on demand.
function usePrecacheFonts() {
  useEffect(() => {
    if (onMeteredOrSlowConnection()) return
    const families = new Set(precachedFontFamilies.map(bareFamily))
    const precache = () => {
      for (const face of document.fonts) {
        if (
          families.has(bareFamily(face.family)) &&
          face.style === "normal" &&
          face.status === "unloaded"
        ) {
          void face.load().catch(() => {})
        }
      }
    }
    let cancelIdle = () => {}
    const scheduleAtIdle = () => {
      if (typeof window.requestIdleCallback === "function") {
        const handle = window.requestIdleCallback(precache)
        cancelIdle = () => window.cancelIdleCallback(handle)
      } else {
        const handle = window.setTimeout(precache, 2000)
        cancelIdle = () => window.clearTimeout(handle)
      }
    }
    if (document.readyState === "complete") {
      scheduleAtIdle()
    } else {
      window.addEventListener("load", scheduleAtIdle, { once: true })
    }
    return () => {
      window.removeEventListener("load", scheduleAtIdle)
      cancelIdle()
    }
  }, [])
}

export function Fonts() {
  "use no memo"
  usePrecacheFonts()
  return (
    <>
      <style jsx global>{`
				:root {
					--font-inter: ${interFontFamily};
					--font-circular-family: ${circularFontFamily};
				}
			}`}</style>
    </>
  )
}
